import { useCallback, useEffect, useRef, useState } from 'react';
import type { BufferedReaction } from '../../shared/bufferedReaction';
import type { ReplayReactionEvent } from '../../shared/replayReactionEvent';
import type { ViewerTestPayload } from '../../shared/viewerTestPayload';
import { ApiRequestError } from '../api/apiRequestError';
import { getReviewerReplayStream } from '../api/getReviewerReplayStream';
import { getViewerTest } from '../api/getViewerTest';
import { postCreateReviewer } from '../api/postCreateReviewer';
import { postReviewerFinish } from '../api/postReviewerFinish';
import { drainOrphanedBatches } from '../player/drainOrphanedBatches';
import { engagementSampler } from '../player/engagementSampler';
import { createReactionBufferStore } from '../player/reactionBufferStore';
import { reviewerSessionStorage } from '../player/reviewerSessionStorage';
import { youtubeIframeController } from '../player/youtubeIframeController';
import { PortraitOrientationNotice } from '../portraitOrientationNotice';
import { ReactionReplayOverlay } from '../replay/reactionReplayOverlay';
import { ViewerLegendScreen } from './viewerLegendScreen';
import { ViewerReturningScreen } from './viewerReturningScreen';
import { ViewerSummaryScreen } from './viewerSummaryScreen';
import { ViewerWatchScreen } from './viewerWatchScreen';

type FlowStage = 'loading' | 'returning' | 'legend' | 'watch' | 'summary' | 'error';

type PlayerHandle = Awaited<ReturnType<typeof youtubeIframeController>>;

// Owns the whole reviewer journey: legend -> watch -> summary.
//
// All three screens stay mounted and are toggled with a class, exactly as the
// prototype does. This is not laziness — the YouTube iframe lives inside the
// watch screen, and unmounting it to change screens would tear down and reload
// the player, losing the playhead and the reviewer's place in the video.
export function ViewerSessionFlow(input: { testId: string }) {
  const [stage, setStage] = useState<FlowStage>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [test, setTest] = useState<ViewerTestPayload | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [playerReady, setPlayerReady] = useState(false);
  const [tapCount, setTapCount] = useState(0);
  const [reviewerId, setReviewerId] = useState<string | null>(null);
  const [ownReactions, setOwnReactions] = useState<BufferedReaction[]>([]);
  const [durationMs, setDurationMs] = useState(0);
  const [furthestMs, setFurthestMs] = useState(0);
  const [replayEvents, setReplayEvents] = useState<ReplayReactionEvent[] | null>(null);
  // Live mode only. Held separately from replayEvents so the summary-screen
  // replay and the during-watch overlay cannot be confused for one another.
  const [liveEvents, setLiveEvents] = useState<ReplayReactionEvent[]>([]);
  // The regime this reviewer is recorded under, once their session has started.
  // Null before that. The render falls back to test.mode, which is right for the
  // pre-start screens: nobody is recorded yet, so the test's mode IS what they
  // are about to get.
  const [watchedMode, setWatchedMode] = useState<'blind' | 'live' | null>(null);

  const playerMountRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<PlayerHandle | null>(null);
  const bufferRef = useRef<ReturnType<typeof createReactionBufferStore> | null>(null);
  const stopSamplerRef = useRef<(() => void) | null>(null);
  const reviewerIdRef = useRef<string | null>(null);
  const testIdRef = useRef<string | null>(null);
  testIdRef.current = input.testId;
  const finishedRef = useRef(false);
  const bootedRef = useRef(false);

  const finishSession = useCallback(async () => {
    if (finishedRef.current) return;
    finishedRef.current = true;

    stopSamplerRef.current?.();
    stopSamplerRef.current = null;

    const player = playerRef.current;
    player?.pauseVideo();

    const measuredDuration = player?.durationMs() ?? 0;
    if (measuredDuration > 0) setDurationMs(measuredDuration);
    setStage('summary');

    const buffer = bufferRef.current;
    if (buffer) await buffer.stop();

    const currentReviewerId = reviewerIdRef.current;
    if (currentReviewerId) {
      // Recorded before the request, and kept even if it fails: the reviewer IS
      // done either way, and the thing this flag prevents is a duplicate row on
      // their next visit.
      const testId = testIdRef.current;
      if (testId) {
        const existing = reviewerSessionStorage.read(testId);
        reviewerSessionStorage.write(testId, {
          reviewer_id: currentReviewerId,
          finished: true,
          next_seq: existing?.next_seq ?? 1,
          // Carried forward, not recomputed. This runs on unload and must not
          // rewrite the regime the session was actually conducted under.
          mode: existing?.mode ?? null
        });
      }
      try {
        await postReviewerFinish(currentReviewerId, measuredDuration);
      } catch {
        // The reviewer is done either way and their summary is already on screen.
        // finished_at is what unlocks replay, so the button there may 403 until
        // this lands — which is the correct failure, not a lost session.
      }
    }
  }, []);

  // STEP 1 — fetch the test.
  //
  // Deliberately does NOT touch playerMountRef. It used to, and that was a bug
  // that shipped: the mount node lives inside ViewerWatchScreen, which is not
  // rendered at all while stage === 'loading'. Reading the ref on the line after
  // setStage('legend') read it BEFORE React had committed that render, so it was
  // always null, the effect returned early, and the player was never created —
  // leaving the Start button on "Loading video…" forever. Deterministic, silent,
  // and invisible to an API-level smoke test.
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    let cancelled = false;

    const loadTest = async () => {
      // Anything a previous session left unsent goes out first (spec §4's
      // localStorage mirror). Not awaited into the critical path — a reviewer
      // should not wait on someone else's stale buffer.
      void drainOrphanedBatches(null);

      try {
        const payload = await getViewerTest(input.testId);
        if (cancelled) return;
        setTest(payload);

        // Cowork016 §1/§2. If this browser already finished this test, never show
        // the legend — that is the screen with the Start button, and reaching it
        // is how a second reviewer row gets created.
        const previous = reviewerSessionStorage.read(input.testId);
        if (previous?.finished) {
          reviewerIdRef.current = previous.reviewer_id;
          setReviewerId(previous.reviewer_id);
          setStage('returning');
          return;
        }
        setStage('legend');
      } catch (caught) {
        if (cancelled) return;
        setErrorMessage(caught instanceof ApiRequestError ? caught.message : 'Could not load that test.');
        setStage('error');
      }
    };

    void loadTest();
    return () => {
      cancelled = true;
    };
  }, [input.testId]);

  // STEP 2 — build the player, once `test` exists.
  //
  // Because this effect depends on `test`, React has already committed the render
  // that mounts ViewerWatchScreen by the time it runs, so playerMountRef.current
  // is populated. That ordering is the whole point of the split; do not merge
  // this back into the fetch.
  useEffect(() => {
    if (!test) return;
    if (playerRef.current) return;

    const mount = playerMountRef.current;
    if (!mount) {
      // Should be unreachable. Say so loudly rather than hanging on a disabled
      // button, which is exactly how the original bug hid.
      setErrorMessage('The video player could not be placed on the page. Please refresh.');
      setStage('error');
      return;
    }

    let cancelled = false;

    // YT.Player REPLACES the element it is given with an iframe. Handing it a
    // node React rendered would leave React reconciling against a DOM node that
    // no longer exists, so it gets a child created outside React's knowledge.
    const host = document.createElement('div');
    mount.appendChild(host);

    // The player reports ready through a callback that, for an unembeddable or
    // blocked video, simply never fires — no error, no rejection. Without a
    // watchdog the reviewer sits on a disabled "Loading video…" button with
    // nothing to do and nothing to report. Generous, because a cold cache on a
    // phone genuinely can take a few seconds.
    const watchdog = window.setTimeout(() => {
      if (cancelled || playerRef.current) return;
      setErrorMessage(
        "The video didn't load. It may be set to Private, which can't be embedded — " +
          'ask whoever sent you this link to check. Otherwise, refresh to try again.'
      );
      setStage('error');
    }, 20000);

    const build = async () => {
      try {
        const player = await youtubeIframeController({
          mount: host,
          videoId: test.youtube_id,
          showControls: true,
          onEnded: () => void finishSession()
        });
        if (cancelled) {
          player.destroy();
          return;
        }
        window.clearTimeout(watchdog);
        playerRef.current = player;
        setPlayerReady(true);
      } catch {
        if (cancelled) return;
        window.clearTimeout(watchdog);
        setErrorMessage('The video player could not load. Check your connection and refresh.');
        setStage('error');
      }
    };

    void build();
    return () => {
      cancelled = true;
      window.clearTimeout(watchdog);
    };
  }, [test, finishSession]);

  const recordReaction = useCallback((iconId: string, method: 'tap' | 'key') => {
    const player = playerRef.current;
    const buffer = bufferRef.current;
    if (!player || !buffer) return;
    // RAW video time (spec §6.1). Lag correction is a read-time concern and must
    // never be applied on the way in.
    buffer.pushReaction({ icon_id: iconId, video_time_ms: player.currentTimeMs(), method });
    setOwnReactions([...buffer.recorded()]);
    setTapCount(buffer.recorded().length);
  }, []);

  const startSession = async () => {
    const player = playerRef.current;
    if (!player || !test) return;

    // An UNFINISHED reviewer returning to this test reuses their existing row
    // rather than creating a second one (Cowork016 §2). A duplicate would inflate
    // reviewer_count, flatten retention with a viewer who watched nothing, and
    // inflate `reach` — the distinct-people count that exists precisely to tell
    // "9 people reacted" from "1 person reacted 9 times".
    const previous = reviewerSessionStorage.read(test.test_id);
    let createdReviewerId: string;
    let startSeq = 1;
    // The regime THIS reviewer is recorded under — not test.mode, which was read
    // at page load and may since have been flipped (Cowork020 §2 removed the mode
    // lock, so that is now an ordinary thing for a creator to do). Everything
    // below branches on this, so what is shown always matches what the server
    // recorded and what the replay gate will enforce.
    let sessionMode: 'blind' | 'live';

    if (previous && !previous.finished) {
      createdReviewerId = previous.reviewer_id;
      // Continue the sequence. Restarting at 1 would collide with the earlier
      // session's rows and be silently dropped by the idempotency key.
      startSeq = previous.next_seq;
      // Resume under the regime they started in. A record written before the
      // regime was stored has none, and falls back to the old behaviour.
      sessionMode = previous.mode ?? test.mode;
    } else {
      try {
        const created = await postCreateReviewer({
          testId: test.test_id,
          displayName: displayName.trim() || null
        });
        createdReviewerId = created.reviewer_id;
        sessionMode = created.mode;
      } catch (caught) {
        setErrorMessage(caught instanceof ApiRequestError ? caught.message : 'Could not start the session.');
        setStage('error');
        return;
      }
    }

    reviewerSessionStorage.write(test.test_id, {
      reviewer_id: createdReviewerId,
      finished: false,
      next_seq: startSeq,
      mode: sessionMode
    });

    reviewerIdRef.current = createdReviewerId;
    setReviewerId(createdReviewerId);
    setWatchedMode(sessionMode);

    const buffer = createReactionBufferStore({
      reviewerId: createdReviewerId,
      testId: test.test_id,
      startSeq,
      onSeqAdvanced: (seq) =>
        reviewerSessionStorage.write(test.test_id, {
          reviewer_id: createdReviewerId,
          finished: false,
          next_seq: seq,
          mode: sessionMode
        })
    });
    buffer.start();
    bufferRef.current = buffer;

    stopSamplerRef.current = engagementSampler({
      readCurrentTimeMs: () => player.currentTimeMs(),
      readPlayerState: () => player.playerState(),
      onSample: (sample) => {
        buffer.pushSample(sample);
        setFurthestMs((current) => Math.max(current, sample.video_time_ms));
      }
    });

    setStage('watch');
    // playVideo is called from within the click handler's task, so iOS treats it
    // as the user gesture it is and does not block playback.
    player.playVideo();
    setDurationMs(player.durationMs());

    // LIVE MODE ONLY (Cowork017 §2). Fetched once, after the reviewer has
    // committed to watching, and never on a blind test — on a blind test this
    // request would be the contamination the gate exists to prevent, and the
    // server would refuse it anyway.
    //
    // Not awaited: a slow fetch must not delay playback. The overlay simply has
    // nothing to draw until it lands.
    if (sessionMode === 'live') {
      void getReviewerReplayStream(createdReviewerId)
        .then(setLiveEvents)
        .catch(() => {
          // The reviewer watches and taps normally; only the overlay is missing.
        });
    }
  };

  if (stage === 'loading') {
    return (
      <main className="pageWrap">
        <p className="pageSub">Loading…</p>
      </main>
    );
  }

  if (stage === 'error' || !test) {
    return (
      <main className="pageWrap">
        <h1>Can't open this</h1>
        <p className="pageSub">{errorMessage ?? 'That link does not lead anywhere.'}</p>
      </main>
    );
  }

  return (
    <>
      <PortraitOrientationNotice />

      <ViewerReturningScreen
        active={stage === 'returning'}
        title={test.title}
        reviewerId={reviewerId ?? ''}
        onOpenReplay={(events) => {
          setReplayEvents(events);
          const player = playerRef.current;
          player?.seekToMs(0);
          player?.playVideo();
        }}
        onSessionGone={() => {
          // Their row is gone server-side; forget it and let them start fresh.
          reviewerSessionStorage.clear(input.testId);
          setReviewerId(null);
          reviewerIdRef.current = null;
          setStage('legend');
        }}
      />

      <ViewerLegendScreen
        active={stage === 'legend'}
        title={test.title}
        icons={test.icons}
        displayName={displayName}
        onDisplayNameChange={setDisplayName}
        onStart={() => void startSession()}
        startDisabled={!playerReady}
        startLabel={playerReady ? 'Start watching' : 'Loading video…'}
      />

      {/* Stays mounted across every stage. The iframe lives inside it, and
          unmounting to change screens would reload the video. */}
      <ViewerWatchScreen
        active={stage === 'watch' || replayEvents !== null}
        mode={replayEvents !== null ? 'replay' : 'capture'}
        icons={test.icons}
        tapCount={tapCount}
        onRecord={recordReaction}
        onFinish={() => void finishSession()}
        onExitReplay={() => {
          playerRef.current?.pauseVideo();
          setReplayEvents(null);
        }}
        replayOverlay={
          <ReactionReplayOverlay
            // Runs during the watch screen on a LIVE test, and during the
            // summary-screen replay on any test. Never during a blind watch.
            enabled={replayEvents !== null || ((watchedMode ?? test.mode) === 'live' && stage === 'watch')}
            events={replayEvents ?? liveEvents}
            icons={test.icons}
            readCurrentTimeMs={() => playerRef.current?.currentTimeMs() ?? 0}
          />
        }
        playerMountRef={playerMountRef}
      />

      <ViewerSummaryScreen
        active={stage === 'summary' && replayEvents === null}
        mode={watchedMode ?? test.mode}
        // The count as it was when this reviewer loaded the page — they are not
        // included in it, which is exactly right for "are you among the first".
        finishedCount={test.finished_count}
        icons={test.icons}
        reactions={ownReactions}
        durationMs={durationMs}
        furthestMs={furthestMs}
        reviewerId={reviewerId}
        onOpenReplay={(events) => {
          setReplayEvents(events);
          const player = playerRef.current;
          player?.seekToMs(0);
          player?.playVideo();
        }}
      />
    </>
  );
}
